import React, { useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { signUp } from '../lib/supabase/auth';
import { supabase } from '../lib/supabase/client';
import { AiOutlineUserAdd, AiOutlineLogin } from 'react-icons/ai';

const SignUpPage = () => {
  const [formData, setFormData] = useState({
    firstName: '',
    lastName: '',
    email: '',
    age: '',
    password: '',
    confirmPassword: '',
  });
  const [error, setError] = useState('');
  const [loading, setLoading] = useState(false);
  const [success, setSuccess] = useState(false);
  const [successMessage, setSuccessMessage] = useState('');
  const navigate = useNavigate();

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData((prev) => ({
      ...prev,
      [name]: value,
    }));
  };

  const handleSignUp = async (e) => {
    e.preventDefault();
    
    // Reset states
    setError('');
    setLoading(true);
    
    // Form validation
    if (!formData.firstName || !formData.lastName || !formData.email || !formData.age) {
      setError('All fields are required');
      setLoading(false);
      return;
    }

    // Validate password match
    if (formData.password !== formData.confirmPassword) {
      setError('Passwords do not match');
      setLoading(false);
      return;
    }
    
    // Validate password strength
    if (formData.password.length < 6) {
      setError('Password must be at least 6 characters long');
      setLoading(false);
      return;
    }

    // Age must be a number
    if (isNaN(parseInt(formData.age))) {
      setError('Age must be a number');
      setLoading(false);
      return;
    }

    try {
      // Prepare user metadata
      const userData = {
        first_name: formData.firstName,
        last_name: formData.lastName,
        age: parseInt(formData.age)
      };

      // Create the auth user in Supabase with user metadata
      const { data, error } = await signUp(
        formData.email, 
        formData.password,
        userData
      );
      
      if (error) {
        // Handle error when email already exists
        if (error.message.includes('email already registered')) {
          setError('An account with this email already exists');
        } else {
          setError(error.message);
        }
        setLoading(false);
        return;
      }
      
      if (data && data.user) {
        setSuccess(true);
        setSuccessMessage(
          "Registration successful! We've sent a confirmation link to your email address. " +
          "Please check your inbox and click the link to verify your account. " +
          "After verification, you'll be directed to complete your profile setup."
        );
      }
    } catch (err) {
      setError('An unexpected error occurred. Please try again.');
      console.error('Sign up error:', err);
    } finally {
      setLoading(false);
    }
  };

  const handleGoToSignIn = () => {
    navigate('/signin');
  };

  return (
    <div className="flex items-center justify-center min-h-screen bg-gray-50 px-4">
      <div className="auth-form__container w-full max-w-md p-8 bg-white rounded-lg shadow-md">
        <div className="auth-form__header mb-8 text-center">
          <img 
            src="/images/penguin-logo.svg" 
            alt="PokketCoach Logo" 
            className="auth-form__logo w-16 h-16 mx-auto mb-2"
            onError={(e) => {
              e.target.onerror = null;
              e.target.src = "data:image/svg+xml,%3Csvg xmlns='http://www.w3.org/2000/svg' width='40' height='40' viewBox='0 0 40 40'%3E%3Ccircle cx='20' cy='20' r='18' fill='%23C0E1F7' stroke='%230A84FF' stroke-width='2'/%3E%3Cpath d='M15 15 Q20 10, 25 15 Q30 20, 25 25 Q20 30, 15 25 Q10 20, 15 15' fill='%230A84FF'/%3E%3Ccircle cx='17' cy='17' r='2' fill='white'/%3E%3Ccircle cx='23' cy='17' r='2' fill='white'/%3E%3C/svg%3E";
            }}
          />
          <h1 className="auth-form__title text-2xl font-bold text-gray-800">Create Your Account</h1>
          <p className="auth-form__subtitle text-gray-600 mt-2">Join PokketCoach today and start your journey</p>
        </div>

        {success ? (
          <div className="auth-form__success">
            <div className="mb-6 p-4 bg-green-100 text-green-700 rounded-md">
              <p className="auth-form__success-message font-medium">Registration successful!</p>
              <p className="auth-form__success-detail mt-2">
                {successMessage || "We've sent a confirmation link to your email address. Please check your inbox and click the link to verify your account."}
              </p>
            </div>
            <button 
              onClick={handleGoToSignIn}
              className="w-full bg-blue-500 hover:bg-blue-600 text-white font-medium py-3 px-4 rounded-md transition-colors flex items-center justify-center"
            >
              <AiOutlineLogin className="mr-2" />
              Go to Sign In
            </button>
          </div>
        ) : (
          <>
            {error && (
              <div className="auth-form__error mb-4 p-3 bg-red-100 text-red-700 rounded-md text-sm">
                {error}
              </div>
            )}

            <form onSubmit={handleSignUp} className="auth-form">
              <div className="auth-form__field mb-4">
                <label htmlFor="firstName" className="auth-form__label block text-sm font-medium text-gray-700 mb-1">
                  First Name
                </label>
                <input
                  id="firstName"
                  name="firstName"
                  type="text"
                  value={formData.firstName}
                  onChange={handleChange}
                  required
                  className="auth-form__input w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-400"
                  placeholder="Enter your first name"
                />
              </div>

              <div className="auth-form__field mb-4">
                <label htmlFor="lastName" className="auth-form__label block text-sm font-medium text-gray-700 mb-1">
                  Last Name
                </label>
                <input
                  id="lastName"
                  name="lastName"
                  type="text"
                  value={formData.lastName}
                  onChange={handleChange}
                  required
                  className="auth-form__input w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-400"
                  placeholder="Enter your last name"
                />
              </div>

              <div className="auth-form__field mb-4">
                <label htmlFor="email" className="auth-form__label block text-sm font-medium text-gray-700 mb-1">
                  Email
                </label>
                <input
                  id="email"
                  name="email"
                  type="email"
                  value={formData.email}
                  onChange={handleChange}
                  required
                  className="auth-form__input w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-400"
                  placeholder="Enter your email"
                />
              </div>

              <div className="auth-form__field mb-4">
                <label htmlFor="age" className="auth-form__label block text-sm font-medium text-gray-700 mb-1">
                  Age
                </label>
                <input
                  id="age"
                  name="age"
                  type="number"
                  min="13"
                  max="120"
                  value={formData.age}
                  onChange={handleChange}
                  required
                  className="auth-form__input w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-400"
                  placeholder="Your age"
                />
              </div>

              <div className="auth-form__field mb-4">
                <label htmlFor="password" className="auth-form__label block text-sm font-medium text-gray-700 mb-1">
                  Password
                </label>
                <input
                  id="password"
                  name="password"
                  type="password"
                  value={formData.password}
                  onChange={handleChange}
                  required
                  className="auth-form__input w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-400"
                  placeholder="Create a password"
                />
                <p className="auth-form__hint text-xs text-gray-500 mt-1">Must be at least 6 characters</p>
              </div>

              <div className="auth-form__field mb-6">
                <label htmlFor="confirmPassword" className="auth-form__label block text-sm font-medium text-gray-700 mb-1">
                  Confirm Password
                </label>
                <input
                  id="confirmPassword"
                  name="confirmPassword"
                  type="password"
                  value={formData.confirmPassword}
                  onChange={handleChange}
                  required
                  className="auth-form__input w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-400"
                  placeholder="Confirm your password"
                />
              </div>

              <button
                type="submit"
                disabled={loading}
                className="auth-form__submit-button w-full bg-blue-500 hover:bg-blue-600 text-white font-medium py-2 px-4 rounded-md transition-colors flex items-center justify-center"
              >
                {loading ? (
                  <span className="auth-form__spinner inline-block w-5 h-5 border-2 border-white border-t-transparent rounded-full animate-spin mr-2"></span>
                ) : (
                  <AiOutlineUserAdd className="mr-2" />
                )}
                {loading ? 'Creating Account...' : 'Create Account'}
              </button>
            </form>
          </>
        )}

        <div className="auth-form__footer mt-6 text-center text-sm">
          <p className="auth-form__redirect-text text-gray-600">
            Already have an account?{' '}
            <Link to="/signin" className="auth-form__redirect-link text-blue-500 hover:text-blue-700 font-medium">
              Sign In
            </Link>
          </p>
        </div>
      </div>
    </div>
  );
};

export default SignUpPage; 